/**
 * AtlasEventPlannerModule — the Event Planner panel (Passport "Events" tab).
 *
 * "Moments, not metrics." This panel is where a gathering is scheduled and where
 * a recurring one earns its way into a ritual:
 *
 *   • Host an event — title, theme, recurrence (one-off / daily / weekly), an
 *     optional Sovereign ticket price, and an attendance goal. A daily/weekly
 *     event opens a ritual that institutionalizes after
 *     RITUAL_INSTITUTIONALIZE_REPS convened sessions (`createEvent`).
 *   • Buy a ticket (`buyTicket`) — money settles ONLY through the P3 escrow; a
 *     free event still issues an attendance row. Check in (`checkIn`) marks you
 *     present for the session in progress.
 *   • Rate the host (`recordSatisfaction`, 0..100) once you have checked in —
 *     clearing EVENT_SATISFACTION_INF_THRESHOLD on average mints the host
 *     Influence (never SOV→INF).
 *   • The host alone can institutionalize a qualifying ritual
 *     (`institutionalizeRitual`) and, once it is institutionalized and drew
 *     RITUAL_MIN_HUMANS_LAST_SESSION+ humans last session, send the Standing
 *     Invitation (`sendStandingInvites`). Any attendee can mute/unmute a
 *     ritual's invites (`muteRitualInvites`) — a mute is silent, with no streaks
 *     or guilt.
 *   • An institutionalized ritual's DETERMINISTIC NPC host greets its regulars
 *     by name (parsed from the replicated `npc_host.memory` JSON — there is no
 *     LLM at launch) and holds a saved seat for the lapsed.
 *
 * Event-driven: re-renders on `atlas:events-updated` (an event/attendance/ritual/
 * mute/host row changed) and `atlas:wallet-updated` (ticket affordability moved).
 * The client only ever MIRRORS these rows; every count, payout, and host memory
 * is server-computed. The UI gates only for UX — the server is the authority and
 * rejects anything invalid.
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import type {
  EventSpec,
  EventAttendance,
  RitualState,
  NpcHostMemory,
} from '@shared/types/events.js';
import {
  RITUAL_INSTITUTIONALIZE_REPS,
  RITUAL_MIN_HUMANS_LAST_SESSION,
  EVENT_SATISFACTION_INF_THRESHOLD,
} from '@shared/constants.js';

/** Short identity hex for compact labels (`abcdef12…`). */
function shortHex(hex: string): string {
  return hex.length > 10 ? `${hex.slice(0, 8)}\u2026` : hex;
}

/** Current wall-clock in micros-since-epoch (matches the server Timestamp unit). */
function nowMicros(): bigint {
  return BigInt(Date.now()) * 1000n;
}

/** A whole, non-negative number from a numeric input, or null if invalid. */
function parseCount(raw: string): number | null {
  const s = raw.trim();
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return Number.isSafeInteger(n) && n >= 0 ? n : null;
}

/** Human-readable absolute time for a micros-since-epoch instant. */
function fmtWhen(micros: bigint): string {
  if (micros <= 0n) return 'unscheduled';
  return new Date(Number(micros / 1000n)).toLocaleString();
}

/** HTML-escape a user-authored string before it touches innerHTML. */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const RECURRENCE_LABEL: Record<string, string> = {
  '': 'One-off',
  daily: 'Daily',
  weekly: 'Weekly',
};

export class AtlasEventPlannerModule implements IModule {
  readonly name = 'AtlasEventPlanner';

  private titleIn!: HTMLInputElement;
  private themeIn!: HTMLInputElement;
  private recurrenceIn!: HTMLSelectElement;
  private priceIn!: HTMLInputElement;
  private targetIn!: HTMLInputElement;
  private createBtn!: HTMLButtonElement;
  private createNote!: HTMLElement;
  private listEl!: HTMLElement;

  init(_app: App): void {
    this.titleIn = document.getElementById('ev-title') as HTMLInputElement;
    this.themeIn = document.getElementById('ev-theme') as HTMLInputElement;
    this.recurrenceIn = document.getElementById('ev-recurrence') as HTMLSelectElement;
    this.priceIn = document.getElementById('ev-price') as HTMLInputElement;
    this.targetIn = document.getElementById('ev-target') as HTMLInputElement;
    this.createBtn = document.getElementById('ev-create-btn') as HTMLButtonElement;
    this.createNote = document.getElementById('ev-create-note') as HTMLElement;
    this.listEl = document.getElementById('ev-list') as HTMLElement;

    this.createBtn.addEventListener('click', () => this.create());
    // Delegate every per-event action button (the list is rebuilt each render).
    this.listEl.addEventListener('click', (e) => this.onListClick(e));

    // An event/attendance/ritual/mute/host row changed — rebuild the list.
    bus.on('atlas:events-updated', () => this.renderList());
    // Our SOV balance changed — ticket affordability may have flipped.
    bus.on('atlas:wallet-updated', () => this.renderList());

    this.renderList();
  }

  // ─── Create ───────────────────────────────────────────────────────────────────

  private create(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const worldId = atlas.currentWorldId;
    if (worldId === null) {
      this.setCreateNote('Enter a world before hosting an event.', 'err');
      return;
    }
    const title = this.titleIn.value.trim();
    if (title.length === 0) {
      this.setCreateNote('Give your event a title.', 'err');
      return;
    }
    const theme = this.themeIn.value.trim();
    const recurrence = this.recurrenceIn.value; // '' | 'daily' | 'weekly'
    const price = parseCount(this.priceIn.value);
    if (price === null) {
      this.setCreateNote('Ticket price must be a whole number of Sovereigns (0 = free).', 'err');
      return;
    }
    const target = parseCount(this.targetIn.value);
    if (target === null) {
      this.setCreateNote('Attendance goal must be a whole number.', 'err');
      return;
    }

    this.setCreateNote(`Scheduling \u201c${title}\u201d\u2026`, '');
    this.createBtn.disabled = true;
    void conn.reducers
      .createEvent({
        worldId,
        title,
        theme,
        startAt: nowMicros(),
        recurrence,
        ticketPriceSov: BigInt(price),
        attendanceTarget: target,
      })
      .then(() => {
        const kind = recurrence === '' ? 'event' : `${RECURRENCE_LABEL[recurrence] ?? recurrence} ritual`;
        this.setCreateNote(`Scheduled \u2014 \u201c${title}\u201d is live as a ${kind}.`, 'ok');
        this.titleIn.value = '';
        this.themeIn.value = '';
      })
      .catch((err: Error) => this.setCreateNote(err.message, 'err'))
      .finally(() => {
        this.createBtn.disabled = false;
      });
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  private renderList(): void {
    const me = atlas.identityHex;
    this.listEl.innerHTML = '';

    const events = [...atlas.events.values()].sort((a, b) => {
      // Soonest first; ties broken by newest creation.
      if (a.startAt !== b.startAt) return a.startAt < b.startAt ? -1 : 1;
      return a.createdAt > b.createdAt ? -1 : 1;
    });

    if (events.length === 0) {
      this.listEl.appendChild(this.empty('No events scheduled yet.'));
      return;
    }

    for (const ev of events) {
      this.listEl.appendChild(this.eventCard(ev, me));
    }
  }

  /** Build one event card with its ritual badges, host greeting, and actions. */
  private eventCard(ev: EventSpec, me: string | null): HTMLElement {
    const att = this.myAttendance(ev.eventId, me);
    const rit = this.ritualFor(ev.eventId);
    const isHost = me !== null && ev.host === me;

    const el = document.createElement('div');
    el.className = 'ev-item';
    el.dataset.eventId = ev.eventId.toString();

    // Header: title + recurrence/ritual badges.
    const badges: string[] = [];
    const recurrenceLabel = RECURRENCE_LABEL[ev.recurrence] ?? ev.recurrence;
    badges.push(`<span class="badge">${esc(recurrenceLabel)}</span>`);
    if (rit) {
      badges.push('<span class="badge ritual">Ritual</span>');
      if (rit.institutionalized) badges.push('<span class="badge inst">Institutionalized</span>');
    }
    const head = document.createElement('div');
    head.className = 'head';
    head.innerHTML = `<span class="title">${esc(ev.title)}</span><span>${badges.join(' ')}</span>`;
    el.appendChild(head);

    // Meta: host · price · checked-in count · when.
    const price =
      ev.ticketPriceSov === 0n
        ? '<span class="free">Free</span>'
        : `<span class="price">${ev.ticketPriceSov.toString()} SOV</span>`;
    const hostLabel = isHost ? 'you' : shortHex(ev.host);
    const themeBit = ev.theme.trim().length > 0 ? ` \u00b7 ${esc(ev.theme)}` : '';
    const meta = document.createElement('div');
    meta.className = 'meta';
    meta.innerHTML =
      `hosted by ${esc(hostLabel)}${themeBit} \u00b7 ${price} \u00b7 ` +
      `${ev.checkedInCount} checked in \u00b7 ${esc(fmtWhen(ev.startAt))}`;
    el.appendChild(meta);

    // NPC host greeting — deterministic, parsed from npc_host.memory JSON.
    if (rit && rit.institutionalized) {
      const greeting = this.hostGreeting(rit);
      if (greeting) {
        const g = document.createElement('div');
        g.className = 'ev-host';
        g.innerHTML = greeting;
        el.appendChild(g);
      }
    }

    // Ritual progress hint for the host (anchored to the canonical threshold).
    if (rit && !rit.institutionalized) {
      const remaining = Math.max(0, RITUAL_INSTITUTIONALIZE_REPS - rit.repetitions);
      const hint = document.createElement('div');
      hint.className = 'meta';
      hint.innerHTML =
        remaining > 0
          ? `Becomes a ritual after ${remaining} more session${remaining === 1 ? '' : 's'} ` +
            `(${rit.repetitions}/${RITUAL_INSTITUTIONALIZE_REPS}).`
          : `Ready to institutionalize (${rit.repetitions}/${RITUAL_INSTITUTIONALIZE_REPS} sessions).`;
      el.appendChild(hint);
    }

    el.appendChild(this.actions(ev, att, rit, isHost, me));
    return el;
  }

  /** The per-event action buttons, gated for UX (server is the authority). */
  private actions(
    ev: EventSpec,
    att: EventAttendance | null,
    rit: RitualState | null,
    isHost: boolean,
    me: string | null,
  ): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'actions';
    const eid = ev.eventId.toString();

    // Buy a ticket — only when we hold no attendance row yet.
    if (!att) {
      const unaffordable = ev.ticketPriceSov > 0n && ev.ticketPriceSov > atlas.walletSov;
      const label = ev.ticketPriceSov === 0n ? 'Get free ticket' : `Buy ticket (${ev.ticketPriceSov.toString()} SOV)`;
      wrap.appendChild(this.btn('buy', eid, label, 'primary', unaffordable));
    } else {
      // Check in — present for the session in progress.
      wrap.appendChild(this.btn('checkin', eid, att.checkedIn ? 'Checked in \u2713' : 'Check in', '', false));

      // Rate the host — only after checking in, once.
      if (att.checkedIn && !att.satisfactionRecorded) {
        wrap.appendChild(this.rating(eid));
      }
    }

    // Ritual lifecycle — host-only.
    if (rit && isHost) {
      if (!rit.institutionalized) {
        const notReady = rit.repetitions < RITUAL_INSTITUTIONALIZE_REPS;
        wrap.appendChild(this.btn('institutionalize', eid, 'Institutionalize', '', notReady));
      } else {
        // humansLastSession is u32 (always ≥ 0); comparing < 0 is always false.
        // Gate on RITUAL_MIN_HUMANS_LAST_SESSION so a dead ritual's button is
        // disabled — the server enforces this too, but the UX should preview it.
        const cold = rit.humansLastSession < RITUAL_MIN_HUMANS_LAST_SESSION;
        wrap.appendChild(this.btn('invite', eid, 'Send standing invites', '', cold));
      }
    }

    // Mute / unmute the ritual's Standing Invitations — any attendee, silent.
    if (rit) {
      const muted = this.isMuted(rit.ritualId, me);
      wrap.appendChild(this.btn('mute', eid, muted ? 'Unmute invites' : 'Mute invites', '', false));
    }

    return wrap;
  }

  /** Five star buttons mapping to satisfaction scores 20/40/60/80/100. */
  private rating(eid: string): HTMLElement {
    const rate = document.createElement('span');
    rate.className = 'ev-rate';
    rate.title = `Rate the host (clear ${EVENT_SATISFACTION_INF_THRESHOLD} on average to reward them)`;
    for (let stars = 1; stars <= 5; stars++) {
      const b = document.createElement('button');
      b.dataset.act = 'rate';
      b.dataset.eventId = eid;
      b.dataset.score = String(stars * 20);
      b.textContent = '\u2605';
      rate.appendChild(b);
    }
    return rate;
  }

  private btn(
    act: string,
    eid: string,
    label: string,
    extraClass: string,
    disabled: boolean,
  ): HTMLButtonElement {
    const b = document.createElement('button');
    if (extraClass) b.className = extraClass;
    b.dataset.act = act;
    b.dataset.eventId = eid;
    b.textContent = label;
    b.disabled = disabled;
    return b;
  }

  // ─── Actions (delegated) ───────────────────────────────────────────────────────

  private onListClick(e: MouseEvent): void {
    const target = e.target as HTMLElement | null;
    const btn = target?.closest('button[data-act]') as HTMLButtonElement | null;
    if (!btn || btn.disabled) return;
    const eid = btn.dataset.eventId;
    if (!eid) return;
    const eventId = BigInt(eid);
    switch (btn.dataset.act) {
      case 'buy':
        this.buyTicket(eventId);
        break;
      case 'checkin':
        this.checkIn(eventId);
        break;
      case 'rate':
        this.rate(eventId, Number(btn.dataset.score ?? '0'));
        break;
      case 'institutionalize':
        this.institutionalize(eventId);
        break;
      case 'invite':
        this.sendInvites(eventId);
        break;
      case 'mute':
        this.toggleMute(eventId);
        break;
    }
  }

  private buyTicket(eventId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .buyTicket({ eventId })
      .then(() => this.setCreateNote('Ticket secured \u2014 see you there.', 'ok'))
      .catch((err: Error) => this.setCreateNote(err.message, 'err'));
  }

  private checkIn(eventId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .checkIn({ eventId })
      .then(() => this.setCreateNote('Checked in \u2014 enjoy the gathering.', 'ok'))
      .catch((err: Error) => this.setCreateNote(err.message, 'err'));
  }

  private rate(eventId: bigint, score: number): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .recordSatisfaction({ eventId, score })
      .then(() => this.setCreateNote(`Thanks \u2014 you rated this gathering ${score}/100.`, 'ok'))
      .catch((err: Error) => this.setCreateNote(err.message, 'err'));
  }

  private institutionalize(eventId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    const rit = this.ritualFor(eventId);
    if (!rit) return;
    void conn.reducers
      .institutionalizeRitual({ ritualId: rit.ritualId })
      .then(() => this.setCreateNote('Institutionalized \u2014 your ritual now has a host who remembers.', 'ok'))
      .catch((err: Error) => this.setCreateNote(err.message, 'err'));
  }

  private sendInvites(eventId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    const rit = this.ritualFor(eventId);
    if (!rit) return;
    void conn.reducers
      .sendStandingInvites({ ritualId: rit.ritualId })
      .then(() => this.setCreateNote('Standing Invitations sent to the regulars who opted in.', 'ok'))
      .catch((err: Error) => this.setCreateNote(err.message, 'err'));
  }

  private toggleMute(eventId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    const rit = this.ritualFor(eventId);
    if (!rit) return;
    const wasMuted = this.isMuted(rit.ritualId, atlas.identityHex);
    void conn.reducers
      .muteRitualInvites({ ritualId: rit.ritualId })
      .then(() =>
        this.setCreateNote(wasMuted ? 'Invites unmuted for this ritual.' : 'Invites muted \u2014 quietly, no nag.', 'ok'),
      )
      .catch((err: Error) => this.setCreateNote(err.message, 'err'));
  }

  // ─── Lookups ───────────────────────────────────────────────────────────────────

  /** The local player's attendance row for an event, or null. */
  private myAttendance(eventId: bigint, me: string | null): EventAttendance | null {
    if (me === null) return null;
    for (const a of atlas.attendances.values()) {
      if (a.eventId === eventId && a.identity === me) return a;
    }
    return null;
  }

  /** The ritual bound to an event (recurring events only), or null. */
  private ritualFor(eventId: bigint): RitualState | null {
    for (const r of atlas.rituals.values()) {
      if (r.eventId === eventId) return r;
    }
    return null;
  }

  /** True when the local player has a Standing-Invitation mute for this ritual. */
  private isMuted(ritualId: bigint, me: string | null): boolean {
    if (me === null) return false;
    for (const m of atlas.ritualMutes.values()) {
      if (m.ritualId === ritualId && m.identity === me) return true;
    }
    return false;
  }

  /**
   * Render the deterministic NPC host's greeting for the local player by parsing
   * the bound host's replicated memory JSON. Greets the player by name when they
   * are a remembered regular, and announces a saved seat when they have lapsed.
   */
  private hostGreeting(rit: RitualState): string | null {
    const host = atlas.npcHosts.get(rit.npcHostId.toString());
    if (!host) return null;
    let mem: NpcHostMemory;
    try {
      mem = JSON.parse(host.memory) as NpcHostMemory;
    } catch {
      return null;
    }
    const regulars = Array.isArray(mem.regulars) ? mem.regulars : [];
    const myName = atlas.username;
    const mine = myName ? regulars.find((r) => r.username === myName) : undefined;
    if (mine) {
      if (mine.savedSeat) {
        return `\u{1FA91} The host saved your seat \u2014 <b>good to see you again, ${esc(mine.username)}</b>.`;
      }
      return `The host greets you by name \u2014 <b>${esc(mine.username)}</b>, ${mine.sessionsAttended} session${mine.sessionsAttended === 1 ? '' : 's'} together.`;
    }
    const n = regulars.length;
    if (n === 0) return `The host is here, ready to remember the regulars.`;
    return `The host remembers ${n} regular${n === 1 ? '' : 's'} across ${mem.totalSessions} session${mem.totalSessions === 1 ? '' : 's'}.`;
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────────

  private empty(text: string): HTMLElement {
    const el = document.createElement('div');
    el.className = 'empty';
    el.textContent = text;
    return el;
  }

  private setCreateNote(text: string, cls: '' | 'ok' | 'err'): void {
    this.createNote.className = cls ? `note ${cls}` : 'note';
    this.createNote.textContent = text;
  }
}
